import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Calendar, Clock, Edit, Trash2, ArrowLeft, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const SavedEventsPage = () => {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_URL = window.location.origin;
  const token = localStorage.getItem('sessionToken');

  useEffect(() => {
    fetchDrafts();
  }, []);

  const fetchDrafts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events/drafts`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data && response.data.drafts) {
        setDrafts(response.data.drafts);
      }
    } catch (error) {
      console.error('Error fetching drafts:', error);
      toast.error('Failed to load saved events');
    } finally {
      setLoading(false);
    }
  };

  const handleEditDraft = (draft) => {
    // Navigate to create event page with draft data
    navigate('/create-event', {
      state: {
        fee_id: draft.fee_id,
        draft_id: draft.id
      }
    });
  };

  const handleDeleteDraft = async (draftId) => {
    if (!window.confirm('Are you sure you want to delete this saved event?')) {
      return;
    }

    try {
      const response = await axios.delete(`${API_URL}/api/events/drafts/${draftId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data && response.data.success) {
        toast.success('Saved event deleted');
        fetchDrafts();
      } else {
        toast.error('Failed to delete saved event');
      }
    } catch (error) {
      console.error('Error deleting draft:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete saved event';
      toast.error(errorMessage);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-contribo-navy via-contribo-navy to-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-contribo-navy via-contribo-navy to-gray-900 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-contribo-navy to-contribo-gold p-4 shadow-xl">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/host-cohost-dashboard')}
              className="text-white hover:bg-white hover:bg-opacity-20"
            >
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center gap-2">
              <Save className="text-white" size={28} />
              <h1 className="text-2xl sm:text-3xl font-bold text-white">Saved Events</h1>
            </div>
          </div>
          <p className="text-sm text-white text-opacity-90 ml-11">
            Resume your event drafts and continue editing
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-3 py-6">
        {drafts.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="mx-auto text-gray-400 mb-4" size={64} />
            <h2 className="text-2xl font-bold text-white mb-2">No Saved Events</h2>
            <p className="text-gray-300 mb-6">
              Start creating an event and save it as a draft to continue later
            </p>
            <Button
              onClick={() => navigate('/select-plan')}
              className="bg-contribo-gold text-contribo-navy hover:bg-yellow-500"
            >
              Create New Event
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {drafts.map((draft) => (
              <Card
                key={draft.id}
                className="bg-white shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => handleEditDraft(draft)}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-start justify-between gap-2">
                    <span className="line-clamp-2">
                      {draft.title || 'Untitled Event'}
                    </span>
                    <Save className="text-yellow-600 flex-shrink-0" size={20} />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Event Date */}
                  {draft.date && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar size={16} className="text-blue-600" />
                      <span>
                        {formatDate(draft.date)}
                        {draft.time && ` at ${draft.time}`}
                      </span>
                    </div>
                  )}

                  {/* Location */}
                  {draft.location && (
                    <p className="text-sm text-gray-600 line-clamp-1">
                      📍 {draft.location}
                    </p>
                  )}

                  {/* Description */}
                  {draft.description && (
                    <p className="text-xs text-gray-500 line-clamp-2">
                      {draft.description}
                    </p>
                  )}

                  {/* Goals Count */}
                  {draft.contribution_goals && draft.contribution_goals.length > 0 && (
                    <div className="text-xs text-purple-600 font-semibold">
                      {draft.contribution_goals.length} goal(s) added
                    </div>
                  )}

                  {/* Last Updated */}
                  <div className="flex items-center gap-1 text-xs text-gray-400 pt-2 border-t">
                    <Clock size={12} />
                    <span>Last saved: {formatDateTime(draft.updated_at)}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditDraft(draft);
                      }}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm"
                      size="sm"
                    >
                      <Edit size={14} className="mr-1" />
                      Resume
                    </Button>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDraft(draft.id);
                      }}
                      variant="outline"
                      className="text-red-600 hover:bg-red-50 border-red-300 text-sm"
                      size="sm"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedEventsPage;
